import {User} from '@innobile/authmodule';
import {IDropdownElement} from '../shared/dropdown/dropdown-element.interface';

/** A company as the app stores it — `/authentication`'s entry reshaped for the dropdowns. */
export interface Company extends IDropdownElement {
  /** IANA zone id, e.g. "America/Indiana/Indianapolis". Missing on users stored before it shipped. */
  timezone?: string;
}

/**
 * The signed-in user and their companies. Exactly one company is always selected: the app works in
 * one company at a time, so every request carries that one scope and every "now" its timezone.
 */
export class CompanyUser extends User {
  private companies: Company[];

  constructor(raw) {
    super(raw);
    // A raw `/authentication` company carries `name`; a rehydrated stored one already has
    // `title` and passes through verbatim, so extra fields (`timezone`) round-trip for free.
    this.companies = raw.companies.map(c => c.title ? c : ({...c, title: c.name}));
    let stored: number[] = [];
    try {
      stored = JSON.parse(localStorage.getItem('selectedCompanies') ?? '[]');
    } catch (ex) {
      localStorage.removeItem('selectedCompanies');
    }
    this.selectOne(this.companies.find(c => Array.isArray(stored) && stored.includes(c.id)));
  }

  /**
   * Selects `company`, and only it; without one (none stored, or a stored id the user no longer
   * has) the first company. A selection saved when several could be selected keeps its first.
   */
  private selectOne(company: Company | undefined): void {
    const pick = company ?? this.companies[0];
    this.companies.forEach(c => c.selected = c === pick);
  }

  destroy() {
    localStorage.removeItem('selectedCompanies');
  }

  get Companies(): Company[] {
    return this.companies;
  }

  set Companies(companies: Company[]) {
    this.companies = companies;
    this.selectOne(companies.find(c => c.selected));
    localStorage.setItem('selectedCompanies', JSON.stringify(this.companies.filter(c => c.selected).map(c => c.id)));
  }

  get SelectedCompanies() {
    return this.companies.filter((c) => {
      return c.selected;
    }).map(c => c.id);
  }

  /** The selected company's IANA timezone; `null` when it has none (the browser clock then applies). */
  get SelectedTimezone(): string | null {
    return this.companies.find(c => c.selected)?.timezone ?? null;
  }
}
